/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AddPopUpComponent} from '../../../core/components/pop-ups/add-pop-up/add-pop-up.component';
import {DeletePopUpComponent} from '../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {UpdatePopUpComponent} from '../../../core/components/pop-ups/update-pop-up/update-pop-up.component';
import {ManageScenarioTypeListService} from '../../../services/manage-scenario-type-list.service';

@Component({
    selector: 'app-manage-scenario-type-list',
    templateUrl: './manage-scenario-type-list.component.html',
    styleUrls: ['./manage-scenario-type-list.component.scss'],
    standalone: false
})
export class ManageScenarioTypeListComponent implements OnInit {

  displayedColumns: string[] = ['SCEN_TYPE_ID', 'SCEN_TYPE'];
  columnHeaders: ColDef[] = [
    {field: 'SCEN_TYPE_ID', headerName: 'Scenario Type ID'},
    {field: 'SCEN_TYPE', headerName: 'Scenario Type'},
  ];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  scenarioTypes;
  selectedValue = '';
  showSpinner: boolean;

  constructor(public service: ManageScenarioTypeListService,
              public dialog: MatDialog,
              public snackBar: FabMessageService) {
  }

  ngOnInit(): void {
    this.getScenarioType();
  }

  /**
   * Get all scenario type.
   */
  getScenarioType(): void {
    this.showSpinner = true;
    this.service.getList().subscribe(
      (data) => {
        this.scenarioTypes = data;
        this.showSpinner = false;
      },
      (error) => {
        this.errorHandle(error);
      });
  }

  /**
   * Event for selecting a scenario type
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedValue = (event.data.SCEN_TYPE);
    } else if (event.data.SCEN_TYPE === this.selectedValue) {
      this.selectedValue = '';
    }
  }

  /**
   * Update selected Scenario Type with new value.
   */
  updateValue(): void {
    const dialogRef = this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update',
        currentValueToUpdate: this.selectedValue,
        hidePreview: true,
        maxLength: 50
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        this.service.updateData(res.data.newValue, this.selectedValue).subscribe({
          error: error => {
            this.errorHandle(error);
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully Updated.', DISMISS_MSG);
            this.getScenarioType();
            this.selectedValue = '';
          }
        });
      }
    });
  }

  /**
   * Add a new Scenario Type in DB.
   */
  addValue(): void {
    const dialogRef = this.dialog.open(AddPopUpComponent, {
      data: {
        title: 'Add',
        maxLength: 50
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res) {
        this.service.addData(res.data.newValue).subscribe({
          error: error => {
            this.errorHandle(error);
          },
          complete: () => {
            this.showSpinner = false;
            this.snackBar.displaySuccess('Successfully Added.', DISMISS_MSG);
            this.selectedValue = '';
            this.getScenarioType();
          }
        });
      }
    });
  }

  /**
   * Delete a selected Scenario Type from DB.
   */
  deleteValue(): void {
    const dialogRef = this.dialog.open(DeletePopUpComponent, {
      data: {
        title: 'Delete',
        valueToDelete: this.selectedValue
      }
    });

    dialogRef.afterClosed().subscribe(res => {
      // received data from dialog-component
      if (res.data.deleteValue) {
        this.service.deleteData(this.selectedValue).subscribe({
          error: error => {
            this.errorHandle(error);
            this.getScenarioType();
          },
          complete: () => {
            this.snackBar.displaySuccess('Successfully deleted Scenario Type.', DISMISS_MSG);
            this.getScenarioType();
            this.selectedValue = '';
          }
        });
      }
    });
  }

  errorHandle(error: string): void {
    this.showSpinner = false;
    this.selectedValue = '';
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
